import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
// api
import ApiKey from '../api/ApiKey';
import BaseApi from '../api/BaseApi';
// components
import Sidebar from './Sidebar';
import Footer from '../element/Footer';
import NavBar from '../element/NavBar';
import SmallLoading from '../element/SmallLoading';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../helpers';

const ManagersList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('global');
  const userType = Cookies.get('user_type');
  const tokenKey = Cookies.get('tokenClient');
  const employerRole = Cookies.get('employer_role');

  const [inputValue, setInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [managersList, setManagersList] = useState(null);
  const [smallLoading, setSmallLoading] = useState(false);

  const per_page = 15;

  const getManagerRole = role => {
    switch (role) {
      case 'owner':
        return t('managersList.owner');

      case 'manager':
        return t('managersList.manager');

      default: return '';
    }
  };

  const handleSearchChange = useCallback(e => {
    const { value } = e.target;

    setInputValue(value);
    debouncedHandleSearchChange(value);
  }, []); // eslint-disable-line

  const handlePageChange = async page => {
    try {
      setSmallLoading(true);
      setCurrentPage(page);

      const response = await axios.get(
        BaseApi + '/company/managersList',
        {
          params: {
            page,
            per_page,
            search: searchQuery ? searchQuery : null,
          },
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      window.scrollTo(0, 0);

      if (response.data.status === 200) {
        setManagersList(response.data.response.managers.data);
        setPagination(response.data.response.managers.pagination);
      } else {
        resultInfo('error', response.data.message, t('employerCreateJob.close'));
      }

      setSmallLoading(false);
    } catch (error) {
      window.scrollTo(0, 0);

      setSmallLoading(false);

      console.error('Error fetching Jobs List', error);
    }
  };

  const getData = async () => {
    try {
      setSmallLoading(true);

      const response = await axios.get(
        BaseApi + '/company/managersList',
        {
          params: {
            per_page,
            page: currentPage,
            search: searchQuery ? searchQuery : null,
          },
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      if (response.data.status === 200) {
        setManagersList(response.data.response.managers.data);
        setPagination(response.data.response.managers.pagination);
      } else {
        resultInfo('error', response.data.message, t('employerCreateJob.close'));
      }

      setSmallLoading(false);
    } catch (error) {
      setSmallLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      }

      console.error('User details not received!', error);
    }
  };

  const handleDeleteManager = async id => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        showCancelButton: true,
        text: t('managersList.deleteConfirmTxt'),
        title: t('managersList.deleteConfirmTitle'),
        cancelButtonText: t('employerEditProfile.no'),
        confirmButtonText: t('employerEditProfile.yes'),
      });

      if (confirmationResult.isConfirmed) {
        setSmallLoading(true);

        const response = await axios.get(
          BaseApi + `/job/delete/management/${id}`,
          {
            headers: {
              key: ApiKey,
              token: tokenKey,
              contentType: 'application/json',
            },
          },
        );

        setSmallLoading(false);

        if (response.data.status === 200) {
          resultInfo('success', t('managersList.deleteSuccessTitle'), t('employerEditProfile.close'));

          getData();
        } else {
          Swal.fire({
            icon: 'error',
            text: response.data.message,
            title: t('employerEditManager.failedTitle'),
            confirmButtonText: t('employerCreateJob.close'),
          });
        }
      }
    } catch (error) {
      setSmallLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        resultInfo('error', t('employerEditManager.failedTitle'), t('employerEditProfile.close'));
      }

      console.error('Could not submit edit data!', error);
    }
  };

  const debouncedHandleSearchChange = debounce(value => {
    setCurrentPage(1);
    setSearchQuery(value);
  }, 500);

  useEffect(() => {
    if (userType === 'candidate' || !tokenKey) {
      navigate('/user/employerlogin');
    } else {
      getData();

      window.scrollTo(0, 0);
    }
  }, [t, tokenKey, navigate, userType, searchQuery]); // eslint-disable-line

  return (
    <>
      <NavBar />
      <div className='container editProfile'>
        <div className='row'>
          <div className='col-lg-3'>
            <Sidebar />
          </div>
          <div className='col-lg-9 mb-5 CLPanelRight'>
            <div className='mb-5 d-flex PageHeader '>
              <div className='TopHaddingTitle'>
                <div className='TopHaddingTitleSec1'>
                  <h3 className=''>
                    <i>
                      <img
                        alt=''
                        width='34px'
                        src={employerRole === 'owner' ? '/Images/employerSide/icon15color.png' : '/Images/employerSide/icon7.png'}
                      />
                    </i>
                    {employerRole === 'owner' ? t('employerSidebar.managers') : t('employerSidebar.myProfile')}
                  </h3>
                  {
                    employerRole === 'owner'
                      ? (
                        <Link to='/company/add-manager'>
                          <button type='button' className='btn btn-primary button1'>
                            {t('managersList.addManager')}
                          </button>
                        </Link>
                      ) : ''
                  }
                </div>
              </div>
            </div>
            {
              employerRole === 'owner'
                ? (
                  <div className='manageSubadminPart1'>
                    <form onSubmit={e => e.preventDefault()}>
                      <div className='manageSubadminPart1Sec1'>
                        <p style={{ fontSize: '18px' }}>
                          {t('managersList.searchTitle')}
                        </p>
                        <div className='APDashboardSearchBx' style={{ width: '100%' }}>
                          <input
                            type='text'
                            id='searchQuery'
                            name='searchQuery'
                            value={inputValue}
                            className='form-control'
                            onChange={handleSearchChange}
                            style={{ width: '100%', minWidth: '100%' }}
                            placeholder={t('employerManageJobs.search')}
                          />
                        </div>
                      </div>
                    </form>
                  </div>
                ) : ''
            }
            <div>
              {
                smallLoading
                  ? (
                    <div className='text-center'>
                      <SmallLoading />
                    </div>
                  ) : (
                    <div className='mb-5 managersList'>
                      {
                        managersList?.length > 0
                          ? (
                            managersList?.map(i => (
                              <div key={i.id} className='managersListBox'>
                                <div className='text-ellipsis'>
                                  <div className='personalInfo'>
                                    <h5 title={`${i.last_name} ${i.first_name}`}>{i.last_name} {i.first_name}</h5>
                                    <p className='text-muted text-ellipsis'>{getManagerRole(i.role)}</p>
                                  </div>
                                </div>
                                <div className='text-ellipsis'>
                                  <div className='personalInfo'>
                                    <h5 title={`${i.last_name} ${i.first_name}`}>{t('managersList.position')}</h5>
                                    <p title={i.position} className='text-muted text-ellipsis'>{i.position}</p>
                                  </div>
                                </div>
                                <div className='text-ellipsis'>
                                  <div className='contactsInfo'>
                                    <h5 className=''>{t('managersList.email')}</h5>
                                    <a title={i.email_address} href={`mailto:${i.email_address}`}>
                                      {i.email_address}
                                    </a>
                                  </div>
                                </div>
                                <div className='text-ellipsis'>
                                  <div className='contactsInfo'>
                                    <h5 className=''>{t('managersList.contactNumber')}</h5>
                                    <a href={`tel:${i.contact}`}>
                                      {i.contact}
                                    </a>
                                  </div>
                                </div>
                                <div className='actionsBox text-ellipsis' style={{ width: '80px' }}>
                                  <Link
                                    className='editLink'
                                    title={t('employerMyProfile.edit')}
                                    to={`/company/edit-manager/${i.id}`}
                                  >
                                    <i className='fa-solid fa-pen-to-square' />{' '}
                                  </Link>
                                  {
                                    employerRole === 'owner'
                                      ? (
                                        <Link
                                          title={t('employerMyProfile.delete')}
                                          onClick={() => handleDeleteManager(i.id)}
                                          style={{ color: 'white', backgroundColor: '#FF0B0F' }}
                                          className={`editLink ${i.role === 'owner' ? 'disabledEditLink' : ''}`}
                                        >
                                          <i className='fa-solid fa-trash' />{' '}
                                        </Link>
                                      ) : ''
                                  }
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className='text-center'>
                              {t('managersList.noManagers')}
                            </div>
                          )
                      }
                      {
                        employerRole === 'owner'
                          ? (
                            <div className='manageJobPagination'>
                              <p className='text-muted empPaginationData'>
                                {t('pagination.NoofRecords')}
                                {' '}
                                {pagination?.from ? pagination?.from : 0} - {pagination?.to ? pagination?.to: 0}
                                {' '}
                                {t('pagination.of')} {pagination?.total ? pagination?.total : 0}
                              </p>
                              <div className='d-flex justify-content-center empPaginationButton'>
                                <button
                                  className='btn navButton1 me-2'
                                  disabled={pagination?.current_page === 1}
                                  onClick={() => handlePageChange(currentPage - 1)}
                                >
                                  {t('pagination.Prev')}
                                </button>
                                <button
                                  className='btn navButton1'
                                  disabled={pagination?.to >= pagination?.total}
                                  onClick={() => handlePageChange(currentPage + 1)}
                                >
                                  {t('pagination.Next')}
                                </button>
                              </div>
                            </div>
                          ) : ''
                      }
                    </div>
                  )
              }
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ManagersList;
